import { observer } from "mobx-react-lite";
import { useFormikContext } from "formik";
import { useMemo } from "react";
import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { getOptionLabel } from "@/lib/utils";
import {
  LOCATION_OPTIONS,
  INDUSTRIAL_CLASSIFICATION,
  KEY_IMPROVEMENT,
} from "@/lib/constants";
import {
  translateCountry,
  INDUSTRY_VALUE_MAP,
  OBJECTIVE_NAME_MAP,
  translateValue,
} from "@/lib/constants/translations.map";
import { useCompanyStore } from "../hooks";
import { GHG_BASELINE_STEP_ID } from "../utils/company.steps";
import { useAuthStore } from "@/features/auth";
import {
  COMPANY_FEATURE_SETTING_ROWS,
  describeFeatureSettingState,
} from "../utils/companyFeatureSettingRows";
import {
  getCompanyFeatureDisplayDefaults,
  getFeatureSettingState,
} from "../utils/companyFeatureSettings";

const CompanySetupSummary = observer((props) => {
  const {
    showSystemSettings = false,
    showGhgBaseline = false,
    systemSettingsStep = null,
  } = props;
  const { t, i18n } = useTran();
  const { values } = useFormikContext();
  const { setStep } = useCompanyStore();
  const { user } = useAuthStore();

  const targetFrequencyLabel =
    values.targetFrequency === "monthly" ? t("monthly") : t("yearly");
  const getEnabledLabel = (value) =>
    value === false ? t("disabled") : t("enabled");

  // Translate country value
  const countryLabel = useMemo(() => {
    const raw = getOptionLabel(values.country, LOCATION_OPTIONS, "—");
    return translateCountry(raw, i18n.language);
  }, [values.country, i18n.language]);

  // Translate industry value
  const industryLabel = useMemo(() => {
    const raw = getOptionLabel(values.industry, INDUSTRIAL_CLASSIFICATION, "—");
    return translateValue(raw, INDUSTRY_VALUE_MAP, i18n.language);
  }, [values.industry, i18n.language]);

  // Translate service countries
  const serviceCountriesLabel = useMemo(() => {
    if (!values.serviceCountries?.length) return "—";
    return values.serviceCountries
      .map((key) => {
        const found = LOCATION_OPTIONS.find((o) => o.key === key);
        return found ? translateCountry(found.value, i18n.language) : key;
      })
      .join(", ");
  }, [values.serviceCountries, i18n.language]);

  // Translate objectives
  const objectivesLabel = useMemo(() => {
    if (!values.objectives?.length) return "—";
    return values.objectives
      .map((key) => {
        const found = KEY_IMPROVEMENT.find((o) => o.key === key);
        return found
          ? translateValue(found.value, OBJECTIVE_NAME_MAP, i18n.language)
          : key;
      })
      .join(", ");
  }, [values.objectives, i18n.language]);

  const allRows = [
    { label: t("company_name"), value: values.companyName || "—", step: 1 },
    {
      label: t("Country"),
      value: countryLabel,
      step: 1,
    },
    ...(showSystemSettings
      ? [
          ...(user?.role === "system_admin"
            ? [
                {
                  label: t("dashboard"),
                  value:
                    values.dashboardVariant === "benchmark"
                      ? t("benchmark")
                      : t("standard"),
                  step: systemSettingsStep,
                },
                {
                  label: t("allow_asset_list"),
                  value: getEnabledLabel(values.allowAssetList === true),
                  step: systemSettingsStep,
                },
                ...COMPANY_FEATURE_SETTING_ROWS.map((row) => ({
                  label: t(row.titleKey),
                  value: describeFeatureSettingState(
                    row,
                    getFeatureSettingState({
                      field: row.field,
                      stored: values.featureSettingsStored || {},
                      edits: values.featureSettingEdits || {},
                    }),
                    getCompanyFeatureDisplayDefaults(user),
                    t,
                  ),
                  step: systemSettingsStep,
                })),
              ]
            : []),
          {
            label: t("target_setup_frequency"),
            value: targetFrequencyLabel,
            step: systemSettingsStep,
          },
          {
            label: t("allow_monthly_yearly_switch"),
            value: getEnabledLabel(values.enableMonthlyYearlySwitch),
            step: systemSettingsStep,
          },
          {
            label: t("auto_switch_target_overview"),
            value: getEnabledLabel(values.enableViewDetailsSwitch),
            step: systemSettingsStep,
          },
          {
            label: t("use_jj_org_selector"),
            value: getEnabledLabel(values.useJJOrganizationSelector),
            step: systemSettingsStep,
          },
          {
            label: t("show_metrics_with_target_data"),
            value: getEnabledLabel(values.showOnlyMetricsWithTargetAndData),
            step: systemSettingsStep,
          },
        ]
      : []),
    {
      label: t("Industry"),
      value: industryLabel,
      step: 2,
    },
    { label: t("Employees"), value: values.employeesRange || "—", step: 3 },
    { label: t("Revenue (USD)"), value: values.revenueRange || "—", step: 3 },
    {
      label: t("Service Countries"),
      value: serviceCountriesLabel,
      step: 4,
    },
    {
      label: t("objectives"),
      value: objectivesLabel,
      step: 4,
    },
    {
      label: t("ghg_emission_baseline"),
      value:
        values.ghgBaselineMode === "custom"
          ? [
              values.ghgBaselineScope1 &&
                `${t("Scope 1")}: ${values.ghgBaselineScope1}`,
              values.ghgBaselineScope2 &&
                `${t("Scope 2")}: ${values.ghgBaselineScope2}`,
              values.ghgBaselineScope3 &&
                `${t("Scope 3")}: ${values.ghgBaselineScope3}`,
            ]
              .filter(Boolean)
              .join(", ") || "—"
          : values.ghgBaselineReportingYear || "—",
      step: 5,
    },
  ];

  const rows = allRows.filter(
    (row) => showGhgBaseline || row.step !== GHG_BASELINE_STEP_ID,
  );

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">
        {t("review_details")}
      </h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {rows.map((row) => (
          <Card
            key={row.label}
            className="border border-border bg-background p-3"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {row.label}
                </p>
                <p className="mt-1 break-words text-sm font-semibold text-foreground">
                  {row.value}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => setStep(row.step)}
              >
                {t("Edit")}
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
});

export default CompanySetupSummary;
